import { AUTH_COOKIE } from '@/utils/constants'

export const CLOSE_PAGE_VIEW_PATH = '/users/sessions/page-views/close'

/**
 * Avisa que a página aberta acabou, para o cronômetro creditar a duração real
 * em vez de perder o tempo da última tela (AC-086).
 *
 * **Por que não é `navigator.sendBeacon`:** o beacon é o único envio que
 * sobrevive ao descarregamento da página, mas só faz POST e não aceita
 * cabeçalho — e `ensureAuthenticated` (libs/middlewares) lê exclusivamente
 * `request.headers.authorization`, sem qualquer leitura de cookie. Somado a
 * isso, o cookie `auth` é do domínio do front, a API vive em outra origem
 * (`NEXT_PUBLIC_API_URL`) e `apps/users/src/app.ts` sobe `cors()` no padrão
 * (`Access-Control-Allow-Origin: *`, sem credenciais). Ou seja: o beacon
 * chegaria sem token e tomaria 401. `fetch` com `keepalive` também sobrevive
 * ao unload, aceita cabeçalho e funciona cross-origin — é o caminho que
 * autentica.
 *
 * **Por que não passa pelo `httpClient`:** o interceptor de resposta manda
 * `window.location.href = '/login'` em qualquer 401, e telemetria de saída
 * jamais pode navegar a pessoa. Fora isso, `fetchOptions` do axios 1.7 é
 * `Record<string, any>` — sem tipo, um erro de digitação em `keepalive`
 * degradaria a requisição em silêncio, que é exatamente o que não pode
 * acontecer aqui. O token é lido do mesmo cookie, do mesmo jeito que
 * `authInterceptorRequest` lê no navegador.
 *
 * Corpo vazio de propósito: o controller identifica a pessoa pelo token e
 * ignora a requisição. Isso também mantém o envio muito abaixo do limite de
 * 64 KiB que a especificação de fetch impõe ao conjunto de requisições
 * `keepalive` em voo.
 */
export async function closePageView(): Promise<void> {
  if (typeof window === 'undefined') return

  const Cookies = (await import('js-cookie')).default
  const token = Cookies.get(AUTH_COOKIE)

  if (!token) return

  try {
    await fetch(`${process.env.NEXT_PUBLIC_API_URL}${CLOSE_PAGE_VIEW_PATH}`, {
      method: 'POST',
      // Navegador sem suporte a `keepalive` apenas ignora a opção: a
      // requisição continua saindo e só corre risco de ser cortada no
      // fechamento real da aba, caso em que a varredura de expiração (AC-087)
      // credita o teto. Nada quebra.
      keepalive: true,
      headers: { authorization: `Bearer ${token}` },
    })
  } catch {
    // Telemetria. Quem está saindo da aba não pode ver erro nenhum, e não há
    // para onde repetir: a página está indo embora.
  }
}
